import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface CartItem {
  id: string;
  product_variant_id: string;
  quantity: number;
  variant?: {
    id: string;
    name: string;
    sku: string;
    product_id: string;
    products: {
      name: string;
      price: number;
      images: string[];
    };
  };
}

interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  addToCart: (variantId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [user, setUser] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>("");

  useEffect(() => {
    // Get or create session ID for guest users
    let sid = localStorage.getItem("cart_session_id");
    if (!sid) {
      sid = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("cart_session_id", sid);
    }
    setSessionId(sid);

    // Check user auth
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user || sessionId) {
      refreshCart();
    }
  }, [user, sessionId]);

  const refreshCart = async () => {
    try {
      let query = supabase
        .from("cart_items")
        .select(`
          id,
          product_variant_id,
          quantity,
          variant:product_variants!inner(
            id,
            name,
            sku,
            product_id,
            products!inner(
              name,
              price,
              images
            )
          )
        `);

      if (user) {
        query = query.eq("user_id", user.id);
      } else {
        query = query.eq("session_id", sessionId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setCartItems(data || []);
    } catch (error) {
      console.error("Error loading cart:", error);
    }
  };

  const addToCart = async (variantId: string, quantity: number) => {
    try {
      // Check if item already exists in cart
      const existingItem = cartItems.find(
        (item) => item.product_variant_id === variantId
      );

      if (existingItem) {
        // Update quantity
        const { error } = await supabase
          .from("cart_items")
          .update({ quantity: existingItem.quantity + quantity })
          .eq("id", existingItem.id);

        if (error) throw error;
      } else {
        // Insert new item
        const insertData: any = {
          product_variant_id: variantId,
          quantity,
        };

        if (user) {
          insertData.user_id = user.id;
        } else {
          insertData.session_id = sessionId;
        }

        const { error } = await supabase
          .from("cart_items")
          .insert(insertData);

        if (error) throw error;
      }

      await refreshCart();
      toast({
        title: "Producto añadido",
        description: "El producto se ha añadido al carrito correctamente",
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast({
        title: "Error",
        description: "No se pudo añadir el producto al carrito",
        variant: "destructive",
      });
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("id", itemId);

      if (error) throw error;
      await refreshCart();
      toast({
        title: "Producto eliminado",
        description: "El producto se ha eliminado del carrito",
      });
    } catch (error) {
      console.error("Error removing from cart:", error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el producto",
        variant: "destructive",
      });
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        await removeFromCart(itemId);
        return;
      }

      const { error } = await supabase
        .from("cart_items")
        .update({ quantity })
        .eq("id", itemId);

      if (error) throw error;
      await refreshCart();
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const clearCart = async () => {
    try {
      let query = supabase.from("cart_items").delete();

      if (user) {
        query = query.eq("user_id", user.id);
      } else {
        query = query.eq("session_id", sessionId);
      }

      const { error } = await query;
      if (error) throw error;
      await refreshCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
